import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../ComponnentsUI/common/Card';
import { GenericPieChart } from '../../../ComponnentsUI/charts/GenericPieChart';
import { CompactDistributionBars } from './HomeKpiCharts';

type DataSource = 'standard' | 'enhanced';

interface Segment {
  name: string;
  value: number;
}

interface PanelData {
  standard: Segment[];
  enhanced: Segment[];
  colorByName: Record<string, string>;
}

const PRESENTATION: PanelData = {
  standard: [
    { name: 'CEPHALIC', value: 80 },
    { name: 'TRANSVERSE', value: 6 },
    { name: 'BREECH', value: 31 },
    { name: 'UNKNOWN', value: 107 },
  ],
  enhanced: [
    { name: 'CEPHALIC', value: 156 },
    { name: 'TRANSVERSE', value: 22 },
    { name: 'BREECH', value: 9 },
  ],
  colorByName: {
    CEPHALIC: '#3B82F6',
    TRANSVERSE: '#8B5CF6',
    BREECH: '#10B981',
    UNKNOWN: '#94A3B8',
  },
};

const PLACENTA: PanelData = {
  standard: [
    { name: 'ANTERIOR', value: 123 },
    { name: 'POSTERIOR', value: 42 },
    { name: 'FUNDAL', value: 4 },
    { name: 'UNKNOWN', value: 55 },
  ],
  enhanced: [
    { name: 'ANTERIOR', value: 84 },
    { name: 'POSTERIOR', value: 103 },
  ],
  colorByName: {
    ANTERIOR: '#3B82F6',
    POSTERIOR: '#8B5CF6',
    FUNDAL: '#F59E0B',
    UNKNOWN: '#94A3B8',
  },
};

const PREGNANCY: PanelData = {
  standard: [
    { name: 'SINGLETON', value: 177 },
    { name: 'UNKNOWN', value: 47 },
  ],
  enhanced: [
    { name: 'SINGLETON', value: 184 },
    { name: 'MULTIPLE', value: 3 },
  ],
  colorByName: {
    SINGLETON: '#3B82F6',
    MULTIPLE: '#8B5CF6',
    UNKNOWN: '#94A3B8',
  },
};

const TRIMESTER: PanelData = {
  standard: [
    { name: 'T2 (13-27w)', value: 66 },
    { name: 'T3 (28-42w)', value: 83 },
    { name: 'No GA', value: 72 },
    { name: 'Outlier', value: 2 },
  ],
  enhanced: [
    { name: 'T1 (<13w)', value: 3 },
    { name: 'T2 (13-27w)', value: 59 },
    { name: 'T3 (28-42w)', value: 125 },
  ],
  colorByName: {
    'T1 (<13w)': '#10B981',
    'T2 (13-27w)': '#3B82F6',
    'T3 (28-42w)': '#8B5CF6',
    'No GA': '#94A3B8',
    Outlier: '#F59E0B',
  },
};

const OVERALL_COVERAGE: Record<DataSource, number> = {
  standard: 68,
  enhanced: 100,
};

interface PanelCoverage {
  key: string;
  labelKey: string;
  standard: number;
  enhanced: number;
}
const PANEL_COVERAGE: PanelCoverage[] = [
  { key: 'presentation', labelKey: 'kpis.dataSource.tooltip.presentation', standard: 52, enhanced: 100 },
  { key: 'placenta', labelKey: 'kpis.dataSource.tooltip.placenta', standard: 75, enhanced: 100 },
  { key: 'pregnancy', labelKey: 'kpis.dataSource.tooltip.pregnancy', standard: 79, enhanced: 100 },
  { key: 'trimester', labelKey: 'kpis.dataSource.tooltip.trimester', standard: 67, enhanced: 100 },
];

const paletteFor = (panel: PanelData, source: DataSource): string[] =>
  panel[source].map((seg) => panel.colorByName[seg.name] ?? '#CBD5E1');

const SegmentedToggle: React.FC<{
  value: DataSource;
  onChange: (v: DataSource) => void;
  standardLabel: string;
  enhancedLabel: string;
}> = ({ value, onChange, standardLabel, enhancedLabel }) => (
  <div
    className="inline-flex rounded-full border border-gray-200 dark:border-gray-700 bg-card-color-100 dark:bg-gray-800 p-1"
    role="tablist"
  >
    {(
      [
        ['standard', standardLabel],
        ['enhanced', enhancedLabel],
      ] as const
    ).map(([key, label]) => {
      const selected = value === key;
      return (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(key)}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            selected
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-300 hover:bg-card-color-200 dark:hover:bg-gray-700'
          }`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const CoverageIndicator: React.FC<{
  source: DataSource;
  determinateLabel: string;
  perPanelLabel: string;
  panelLabels: Record<string, string>;
}> = ({ source, determinateLabel, perPanelLabel, panelLabels }) => {
  const [open, setOpen] = useState(false);
  const pct = OVERALL_COVERAGE[source];
  return (
    <div className="relative inline-flex items-center gap-2">
      <div className="inline-flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300">
        <span>{determinateLabel}:</span>
        <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100">
          {pct}%
        </span>
      </div>
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label={perPanelLabel}
        className="w-4 h-4 inline-flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-[10px] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        i
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 z-10 w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-lg text-xs">
          <div className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
            {perPanelLabel}
          </div>
          <div className="space-y-1">
            {PANEL_COVERAGE.map((p) => (
              <div
                key={p.key}
                className="flex justify-between items-center gap-3"
              >
                <span className="text-gray-600 dark:text-gray-300">
                  {panelLabels[p.key]}
                </span>
                <span className="tabular-nums text-gray-700 dark:text-gray-200">
                  {p.standard}% → {p.enhanced}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PresentationBars: React.FC<{
  source: DataSource;
  countLabel: string;
  labelMap: Record<string, string>;
}> = ({ source, countLabel, labelMap }) => {
  const rows = PRESENTATION[source].map((seg) => ({
    name: labelMap[seg.name] ?? seg.name,
    value: seg.value,
  }));
  return (
    <CompactDistributionBars
      data={rows}
      countLabel={countLabel}
      colors={paletteFor(PRESENTATION, source)}
    />
  );
};

const NamedPie: React.FC<{
  panel: PanelData;
  source: DataSource;
  labelMap?: Record<string, string>;
}> = ({ panel, source, labelMap }) => {
  const data = panel[source].map((seg) => ({
    name: labelMap?.[seg.name] ?? seg.name,
    value: seg.value,
  }));
  return <GenericPieChart data={data} colors={paletteFor(panel, source)} />;
};

export const ClinicalDataToggleRow: React.FC = () => {
  const { t } = useTranslation('Dashboard');
  const [source, setSource] = useState<DataSource>('enhanced');

  const presentationLabels: Record<string, string> = {
    CEPHALIC: t('kpis.dataSource.presentation.cephalic'),
    TRANSVERSE: t('kpis.dataSource.presentation.transverse'),
    BREECH: t('kpis.dataSource.presentation.breech'),
    UNKNOWN: t('kpis.dataSource.unknown'),
  };
  const placentaLabels: Record<string, string> = {
    ANTERIOR: t('kpis.dataSource.placenta.anterior'),
    POSTERIOR: t('kpis.dataSource.placenta.posterior'),
    FUNDAL: t('kpis.dataSource.placenta.fundal'),
    UNKNOWN: t('kpis.dataSource.unknown'),
  };
  const pregnancyLabels: Record<string, string> = {
    SINGLETON: t('kpis.dataSource.pregnancy.singleton'),
    MULTIPLE: t('kpis.dataSource.pregnancy.multiple'),
    UNKNOWN: t('kpis.dataSource.unknown'),
  };

  const panelLabels: Record<string, string> = {
    presentation: t('kpis.dataSource.tooltip.presentation'),
    placenta: t('kpis.dataSource.tooltip.placenta'),
    pregnancy: t('kpis.dataSource.tooltip.pregnancy'),
    trimester: t('kpis.dataSource.tooltip.trimester'),
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedToggle
          value={source}
          onChange={setSource}
          standardLabel={t('kpis.dataSource.standard')}
          enhancedLabel={t('kpis.dataSource.enhanced')}
        />
        <CoverageIndicator
          source={source}
          determinateLabel={t('kpis.dataSource.determinate')}
          perPanelLabel={t('kpis.dataSource.perPanel')}
          panelLabels={panelLabels}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 sm:gap-6">
        <Card title={t('charts.topFetalPresentations')}>
          <PresentationBars
            source={source}
            countLabel={t('kpis.charts.count')}
            labelMap={presentationLabels}
          />
        </Card>
        <Card title={t('kpis.charts.placenta')}>
          <NamedPie panel={PLACENTA} source={source} labelMap={placentaLabels} />
        </Card>
        <Card title={t('charts.pregnancyType')}>
          <NamedPie
            panel={PREGNANCY}
            source={source}
            labelMap={pregnancyLabels}
          />
        </Card>
        <Card title={t('kpis.charts.trimester')}>
          <NamedPie panel={TRIMESTER} source={source} />
        </Card>
      </div>
    </div>
  );
};
